import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import cs from 'classnames';

import SettingsButton from '../Settings/SettingsButton/SettingsButton';
import StripePaymentForm from '../StripePaymentForm/StripePaymentForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { stripePublicKey } from 'config';
import { showPromptPromise, showError, showInfo } from 'actions/statusActions';
import { getFlinkPlayData } from 'actions/flinkPlayActions';
import { useTrackFamilyPrepaidTime } from '../PrepaidTracker/PrepaidTracker';
import classes from './ManageSubscriptionPrepaid.module.scss';
import api from 'api';

const stripePromise = loadStripe(stripePublicKey);

const formatDate = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeBillingInterval = (billingInterval, subscriptionType) => {
  if (billingInterval === 'month' || billingInterval === 'months') return 'month';
  if (billingInterval === 'year' || billingInterval === 'years') return 'year';
  if (subscriptionType === 'months') return 'month';
  if (subscriptionType === 'year') return 'year';
  return null;
};

const SUBSCRIPTION_TYPE_VARIABLE = /\{\{\s*subscription(?:Type)?\s*\}\}/i;

const getPlainTranslation = (translate, stringNumber, fallback) => {
  const value = translate(stringNumber, fallback, true);
  return typeof value === 'string' && value.trim() ? value : fallback;
};

const buildSubscriptionRow = (template, subscriptionTypeLabel) => {
  const safeTemplate = String(template || 'Subscription');
  const templateWithVariable = SUBSCRIPTION_TYPE_VARIABLE.test(safeTemplate)
    ? safeTemplate
    : `${safeTemplate} {{subscriptionType}}`;
  const match = templateWithVariable.match(SUBSCRIPTION_TYPE_VARIABLE);
  const variableIndex = match?.index || 0;
  const label = templateWithVariable.slice(0, variableIndex).trim();
  const valueSuffix = templateWithVariable.slice(variableIndex + match[0].length);

  return {
    label: label || 'Subscription',
    value: `${subscriptionTypeLabel}${valueSuffix}`.trim(),
  };
};

const getUniqueProductIds = ({ data, family }) => {
  const ids = [];

  if (family?.productId) {
    ids.push(String(family.productId));
  }

  if (Array.isArray(data?.activeProducts)) {
    data.activeProducts.forEach((item) => {
      const productId = item?.productId || item?._id || item;
      if (productId) ids.push(String(productId));
    });
  }

  if (Array.isArray(data?.purchases)) {
    data.purchases.forEach((item) => {
      const productId = item?.productId?._id || item?.productId;
      if (productId) ids.push(String(productId));
    });
  }

  return Array.from(new Set(ids.filter(Boolean)));
};

const ManageSubscriptionPrepaid = ({
  translate,
  family,
  showInfo,
  showError,
  myAccountValues,
  getFlinkPlayData,
}) => {
  const [processing, setProcessing] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [hoursToBuy, setHoursToBuy] = useState(0);
  const [productName, setProductName] = useState('');
  const [activeProductNames, setActiveProductNames] = useState([]);
  const [didCancelAutoPay, setDidCancelAutoPay] = useState(false);
  const [didMakePayment, setDidMakePayment] = useState(false);
  const [didHandleLocalPayReturn, setDidHandleLocalPayReturn] = useState(false);

  const familyId = family._id;
  const { data, isLoading, refetch } = useTrackFamilyPrepaidTime(familyId);

  const isSubscriptionPlan = data?.purchaseProductType === 'subscription' || data?.purchaseProductType === 'org_subscription';
  const isProductPlan = data?.purchaseProductType === 'product';
  const isMonthlySubscription = isSubscriptionPlan && data?.subscriptionType === 'months';
  const isYearlySubscription = isSubscriptionPlan && data?.subscriptionType === 'year';

  const refreshPrepaidData = useCallback(async () => {
    await getFlinkPlayData();
    await Promise.resolve(refetch());
  }, [getFlinkPlayData, refetch]);

  const finishRefresh = useCallback(async () => {
    await refreshPrepaidData();
    setOpenPayment(false);
    setProcessing(false);
  }, [refreshPrepaidData]);

  useEffect(() => {
    setDidCancelAutoPay(false);
    setDidMakePayment(false);
    setOpenPayment(false);
    setProcessing(false);
    setDidHandleLocalPayReturn(false);
  }, [familyId]);

  const subscriptionId = useMemo(() => data?.subscriptionId || '', [data?.subscriptionId]);

  const prepaidDaysRemaining = useMemo(() => Number(data?.prepaidDaysRemaining || 0), [data?.prepaidDaysRemaining]);

  const paymentAmount = useMemo(() => {
    if (!data) return 0;

    if (isMonthlySubscription) {
      return toNumberOrNull(data.nextPaymentAmount) || toNumberOrNull(data.costPerMonth) || 0;
    }

    if (isYearlySubscription) {
      return toNumberOrNull(data.nextPaymentAmount) || toNumberOrNull(data.totalPrice) || 0;
    }

    if (isProductPlan) {
      return toNumberOrNull(data.totalPrice) || 0;
    }

    return 0;
  }, [data, isMonthlySubscription, isYearlySubscription, isProductPlan]);

  const cancelAtPeriodEnd = useMemo(
    () => Boolean(data?.cancel_at_period_end || data?.cancelAtPeriodEnd),
    [data?.cancel_at_period_end, data?.cancelAtPeriodEnd],
  );

  const priceId = useMemo(() => data?.priceId || '', [data?.priceId]);

  const activeProductIds = useMemo(
    () => getUniqueProductIds({ data, family }),
    [data, family],
  );

  const fallbackExpiresAt = useMemo(() => {
    if (data?.currentPeriodEndsAt) return data.currentPeriodEndsAt;
    if (data?.nextRenewalDate) return data.nextRenewalDate;
    if (data?.expiresAt) return data.expiresAt;

    if (typeof prepaidDaysRemaining === 'number' && !Number.isNaN(prepaidDaysRemaining)) {
      const fallbackDate = new Date();
      fallbackDate.setHours(0, 0, 0, 0);
      fallbackDate.setDate(fallbackDate.getDate() + Math.max(prepaidDaysRemaining, 0));
      return fallbackDate.toISOString();
    }

    return null;
  }, [data?.currentPeriodEndsAt, data?.nextRenewalDate, data?.expiresAt, prepaidDaysRemaining]);

  const billingInterval = useMemo(
    () => normalizeBillingInterval(data?.billingInterval, data?.subscriptionType),
    [data?.billingInterval, data?.subscriptionType],
  );

  const subscriptionTypeLabel = useMemo(() => {
    if (billingInterval === 'month') {
      return getPlainTranslation(translate, 3732, 'Monthly');
    }

    if (billingInterval === 'year') {
      return getPlainTranslation(translate, 3733, 'Yearly');
    }

    return '—';
  }, [billingInterval, translate]);

  const subscriptionRow = useMemo(() => {
    const template = getPlainTranslation(
      translate,
      3670,
      'Subscription {{subscriptionType}}',
    );

    return buildSubscriptionRow(template, subscriptionTypeLabel);
  }, [subscriptionTypeLabel, translate]);

  const currentPeriodEndsAt = useMemo(
    () => data?.currentPeriodEndsAt || data?.nextRenewalDate || data?.expiresAt || fallbackExpiresAt,
    [data?.currentPeriodEndsAt, data?.nextRenewalDate, data?.expiresAt, fallbackExpiresAt],
  );

  const currentPeriodEndsAtLabel = useMemo(
    () => formatDate(currentPeriodEndsAt),
    [currentPeriodEndsAt],
  );

  const nextPaymentAmount = useMemo(() => {
    if (!isSubscriptionPlan) return null;
    const backendAmount = toNumberOrNull(data?.nextPaymentAmount);
    if (backendAmount !== null) return backendAmount;
    return paymentAmount || null;
  }, [isSubscriptionPlan, data?.nextPaymentAmount, paymentAmount]);

  const activeProductsLabel = useMemo(() => {
    if (activeProductNames.length) return activeProductNames.join(', ');
    if (productName) return productName;
    return '—';
  }, [activeProductNames, productName]);

  const managedSubscriptionStatuses = useMemo(
    () => ['active', 'trialing', 'incomplete', 'past_due', 'unpaid'],
    [],
  );

  const automaticPayments = useMemo(() => {
    if (!data || !isSubscriptionPlan) return false;
    if (Boolean(data.automaticPayments || data.autoRenew)) return true;
    return Boolean(
      subscriptionId &&
      managedSubscriptionStatuses.includes(data?.subscriptionStatus) &&
      !cancelAtPeriodEnd,
    );
  }, [
    data,
    isSubscriptionPlan,
    subscriptionId,
    managedSubscriptionStatuses,
    cancelAtPeriodEnd,
  ]);

  const hasRecurringPriceSource = useMemo(() => {
    if (!isSubscriptionPlan) return false;
    if (priceId) return true;

    const candidateLists = [
      data?.priceIds,
      data?.recurringPriceIds,
      data?.activeStripePriceIds,
      data?.stripePriceIds,
    ];

    if (candidateLists.some((list) => Array.isArray(list) && list.some(Boolean))) {
      return true;
    }

    return Boolean(
      Number(nextPaymentAmount || 0) > 0 &&
      activeProductIds.length > 0 &&
      billingInterval,
    );
  }, [
    isSubscriptionPlan,
    priceId,
    data?.priceIds,
    data?.recurringPriceIds,
    data?.activeStripePriceIds,
    data?.stripePriceIds,
    nextPaymentAmount,
    activeProductIds,
    billingInterval,
  ]);

  const rawCanceledState = useMemo(
    () => Boolean(cancelAtPeriodEnd || data?.subscriptionStatus === 'canceled_at_period_end'),
    [cancelAtPeriodEnd, data?.subscriptionStatus],
  );

  const effectiveCanceledState = useMemo(() => {
    if (!isSubscriptionPlan) return false;
    if (didMakePayment) return false;
    if (didCancelAutoPay) return true;
    return rawCanceledState;
  }, [isSubscriptionPlan, didMakePayment, didCancelAutoPay, rawCanceledState]);

  const effectiveSubscriptionStatus = useMemo(() => {
    if (!isSubscriptionPlan) return null;
    if (prepaidDaysRemaining <= 0) return data?.subscriptionStatus || null;
    return effectiveCanceledState ? 'canceled_at_period_end' : 'active';
  }, [
    isSubscriptionPlan,
    prepaidDaysRemaining,
    data?.subscriptionStatus,
    effectiveCanceledState,
  ]);

  const statusLabel = useMemo(() => {
    if (!isSubscriptionPlan) return null;
    const rawStatus = effectiveSubscriptionStatus || data?.subscriptionStatus || null;
    if (!rawStatus) return null;
    if (rawStatus === 'canceled_at_period_end') return translate(3681, 'Canceled');
    if (rawStatus === 'active') return translate(3678, 'Active');
    return rawStatus;
  }, [
    isSubscriptionPlan,
    effectiveSubscriptionStatus,
    data?.subscriptionStatus,
    translate,
  ]);

  const hasManagedSubscription = useMemo(() => {
    if (!isSubscriptionPlan) return false;
    if (prepaidDaysRemaining <= 0) return false;
    if (!currentPeriodEndsAt) return false;
    if (Number(nextPaymentAmount || 0) <= 0) return false;
    return !effectiveCanceledState;
  }, [
    isSubscriptionPlan,
    prepaidDaysRemaining,
    currentPeriodEndsAt,
    nextPaymentAmount,
    effectiveCanceledState,
  ]);

  const canCancelSubscription = useMemo(() => {
    if (!isSubscriptionPlan) return false;
    if (prepaidDaysRemaining <= 0) return false;
    return !effectiveCanceledState;
  }, [isSubscriptionPlan, prepaidDaysRemaining, effectiveCanceledState]);

  useEffect(() => {
    const fetchProductName = async () => {
      const productId = family?.productId;
      if (!productId || productName) return;

      try {
        const result = await api.products.getProductName(productId);
        if (result?.productName) {
          setProductName(result.productName);
        }
      } catch (error) {
        console.error('[ManageSubscriptionPrepaid] productName error', error);
      }
    };

    fetchProductName();
  }, [family?.productId, productName]);

  useEffect(() => {
    let isMounted = true;

    const fetchActiveProductNames = async () => {
      if (!activeProductIds.length) {
        if (isMounted) setActiveProductNames([]);
        return;
      }

      try {
        const results = await Promise.all(
          activeProductIds.map(async (productId) => {
            const result = await api.products.getProductName(productId);
            return result?.productName || null;
          }),
        );

        if (isMounted) {
          setActiveProductNames(Array.from(new Set(results.filter(Boolean))));
        }
      } catch (error) {
        console.error('[ManageSubscriptionPrepaid] activeProductNames error', error);
        if (isMounted) setActiveProductNames([]);
      }
    };

    fetchActiveProductNames();

    return () => {
      isMounted = false;
    };
  }, [activeProductIds]);

  useEffect(() => {
    if (data && hoursToBuy === 0) {
      const hoursLeft = Number((data.totalHours - data.totalHoursPaid).toPrecision(2));
      setHoursToBuy(Math.min(data.hoursInIncrement, hoursLeft));
    }
  }, [data, hoursToBuy]);

  useEffect(() => {
    if (openPayment && isSubscriptionPlan && canCancelSubscription && !processing) {
      setOpenPayment(false);
    }
  }, [openPayment, isSubscriptionPlan, canCancelSubscription, processing]);

  const finalizeSuccessfulPayment = useCallback(async () => {
    if (isMonthlySubscription) {
      await api.family.editPrepaidStatus(family._id, 1, false, null, true);
      return;
    }

    if (isYearlySubscription) {
      await api.family.editPrepaidStatus(family._id, 12, false, null, true);
      return;
    }

    if (isProductPlan) {
      await api.family.editPrepaidStatus(family._id, 1, true);
    }
  }, [family._id, isMonthlySubscription, isYearlySubscription, isProductPlan]);

  const clearLocalPayQuery = () => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete('localPay');
    url.searchParams.delete('session_id');
    url.searchParams.delete('provider');
    url.searchParams.delete('reference');
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  };

  const currency = String(data?.nextPaymentCurrency || data?.currencyIsoCode || 'USD').toUpperCase();

  console.log('[ManageSubscriptionPrepaid][currency]', {
    familyId: family?._id || null,
    nextPaymentCurrency: data?.nextPaymentCurrency || null,
    currencyIsoCode: data?.currencyIsoCode || null,
    resolvedCurrency: currency,
  });

  const startLocalPay = async () => {
    console.log('[ManageSubscriptionPrepaid] startLocalPay from payment form', {
      familyId: family?._id || null,
      paymentAmount,
      currency,
    });

    setProcessing(true);

    try {
      const result = await api.stripe.createFamilyLocalPaySession({
        familyId: family._id,
        email: family.email,
        amount: paymentAmount,
        currency,
        currentUrl: window.location.href,
        purchaseProductType: data?.purchaseProductType || '',
        subscriptionType: data?.subscriptionType || '',
      });

      console.log('[ManageSubscriptionPrepaid] startLocalPay result', result);

      if (!result?.success || !result?.url) {
        setProcessing(false);
        showError({ message: result?.error || 'Unable to start Local Pay right now.' });
        return;
      }

      window.location.href = result.url;
    } catch (error) {
      console.error('[ManageSubscriptionPrepaid] startLocalPay error', error);
      setProcessing(false);
      showError({ message: 'Unable to start Local Pay right now.' });
    }
  };

  useEffect(() => {
    const maybeHandleLocalPayReturn = async () => {
      if (didHandleLocalPayReturn) return;
      if (typeof window === 'undefined') return;
      if (!family?._id || !data) return;

      const url = new URL(window.location.href);
      const isLocalPayReturn = url.searchParams.get('localPay') === '1';
      const provider = String(url.searchParams.get('provider') || 'stripe').trim().toLowerCase();
      const reference = String(
        url.searchParams.get('reference') || url.searchParams.get('session_id') || ''
      ).trim();

      if (!isLocalPayReturn || !reference) return;

      setDidHandleLocalPayReturn(true);
      setProcessing(true);

      try {
        console.log('[ManageSubscriptionPrepaid][local_pay][verify]', { provider, reference });
        const result = await api.stripe.verifyFamilyLocalPay(provider, reference);
        console.log('[ManageSubscriptionPrepaid][local_pay][verify][response]', result);

        console.log('[ManageSubscriptionPrepaid] verifyFamilyLocalPaySession result', result);

        if (!result?.success || result?.paid !== true) {
          setProcessing(false);
          clearLocalPayQuery();
          showError({ message: result?.error || 'Local Pay was not completed.' });
          return;
        }

        const verifiedFamilyId = String(result?.familyId || '').trim();
        const verifiedCurrency = String(result?.currency || '').trim().toUpperCase();
        if (
          result?.type !== 'family'
          || verifiedFamilyId !== String(family._id)
          || verifiedCurrency !== currency
        ) {
          setProcessing(false);
          clearLocalPayQuery();
          showError({ message: 'Local Pay return does not match this family payment.' });
          return;
        }

        await finalizeSuccessfulPayment();
        setDidCancelAutoPay(false);
        setDidMakePayment(true);
        await finishRefresh();
        clearLocalPayQuery();
      } catch (error) {
        console.error('[ManageSubscriptionPrepaid] maybeHandleLocalPayReturn error', error);
        setProcessing(false);
        clearLocalPayQuery();
        showError({ message: 'Unable to verify Local Pay right now.' });
      }
    };

    maybeHandleLocalPayReturn();
  }, [
    didHandleLocalPayReturn,
    family?._id,
    data,
    isMonthlySubscription,
    isYearlySubscription,
    isProductPlan,
    family,
    paymentAmount,
    currency,
    finalizeSuccessfulPayment,
    finishRefresh,
    showError,
  ]);

  const makeStripePayment = async (paymentNonce) => {
    setOpenPayment(false);
    setProcessing(true);

    try {
      if (paymentNonce.status === 'succeeded') {
        await finalizeSuccessfulPayment();
        setDidCancelAutoPay(false);
        setDidMakePayment(true);
        await finishRefresh();
        return;
      }

      await finishRefresh();
      showError({ message: paymentNonce.message });
    } catch (error) {
      console.error('[ManageSubscriptionPrepaid] makeStripePayment error', error);
      setProcessing(false);
      showError({ message: paymentNonce.message || 'Payment error' });
    }
  };

  const requestPaymentMethod = async () => {
    setProcessing(true);

    try {
      if (canCancelSubscription) {
        let resolvedSubscriptionId = subscriptionId;

        if (!resolvedSubscriptionId) {
          try {
            await getFlinkPlayData();
            await new Promise((resolve) => setTimeout(resolve, 100));
            const freshData = await api.family.getPrepaidStatus(family._id);
            resolvedSubscriptionId = freshData?.subscriptionId;
            if (freshData?.subscriptionId) {
              await refetch();
            }
          } catch (error) {
            console.error('[ManageSubscriptionPrepaid] reload before cancel error', error);
          }
        }

        const res = await api.stripe.removeStripeSubscription(
          resolvedSubscriptionId || null,
          family._id,
        );

        const cancelSucceeded =
          Boolean(res?.id) ||
          (
            res?.success === true &&
            (
              res?.status === 'canceled_at_period_end' ||
              res?.cancelAtPeriodEnd === true ||
              res?.cancel_at_period_end === true ||
              res?.localCancelFallback === true
            )
          );

        if (!cancelSucceeded) {
          setProcessing(false);
          showError({ message: res?.error || 'Automatic payments canceled Error!' });
          return;
        }

        if (isMonthlySubscription || isYearlySubscription) {
          await api.family.editPrepaidStatus(
            family._id,
            0,
            false,
            res,
            false,
          );
        }

        setDidCancelAutoPay(true);
        setDidMakePayment(false);
        await finishRefresh();
        return;
      }

      if (isSubscriptionPlan) {
        setProcessing(false);
        setOpenPayment(true);
        return;
      }

      setProcessing(false);
    } catch (error) {
      console.error('[ManageSubscriptionPrepaid] requestPaymentMethod error', error);
      setProcessing(false);
      showError({ message: 'Unable to update payment state right now.' });
    }
  };

  const handleBuyAdditionalProducts = async () => {
    setProcessing(true);

    try {
      const result = await api.family.getAdditionalProductsStatus(family._id);

      if (result?.success && result?.hasAvailableProducts && result?.buyUrl) {
        const url = new URL(result.buyUrl, window.location.origin);

        const allowedProductIds = Array.isArray(result.availableProductIds)
          ? result.availableProductIds.map((v) => String(v)).filter(Boolean)
          : [];

        if (allowedProductIds.length) {
          url.searchParams.set('allowedProductIds', allowedProductIds.join(','));
        }

        if (result.targetLearningCenterId) {
          url.searchParams.set('targetLearningCenterId', String(result.targetLearningCenterId));
        }

        if (family?.email) {
          url.searchParams.set('email', String(family.email).trim());
        }

        window.location.href = url.toString();
        return;
      }

      const resolvedNoAdditionalProductsMessage =
        typeof result?.message === 'string'
          ? result.message
          : (result?.message?.stringValue || 'No additional products are currently available for purchase.');

      showInfo({ message: resolvedNoAdditionalProductsMessage });
    } catch (error) {
      console.error('[ManageSubscriptionPrepaid] additional products error', error);
      showError({ message: 'Unable to open additional products right now.' });
    } finally {
      setProcessing(false);
    }
  };

  const showCancelSubscriptionButton = !openPayment && isSubscriptionPlan && canCancelSubscription;

  const showMakePaymentButton = !openPayment && (
    (isSubscriptionPlan && effectiveCanceledState && Number(paymentAmount || nextPaymentAmount || 0) > 0) ||
    (isProductPlan && !data?.fullyPaid)
  );

  const showAdditionalProductsButton = !openPayment && isSubscriptionPlan;
  const localCurrencyPayments = data?.localCurrencyPayments || {};
  const localCurrencyProviderEnabled = currency === 'USD' || (
    localCurrencyPayments.enabled === true
    && (
      (currency === 'VND' && localCurrencyPayments.stripeVndEnabled !== false)
      || (currency === 'NGN' && localCurrencyPayments.startbuttonNgnEnabled !== false)
    )
  );
  const showLocalPayInPaymentForm =
    openPayment
    && Number(paymentAmount || nextPaymentAmount || 0) > 0
    && localCurrencyProviderEnabled;

  console.log('[ManageSubscriptionPrepaid][renderMode]', {
    purchaseProductType: data?.purchaseProductType,
    fullyPaid: data?.fullyPaid,
    subscriptionType: data?.subscriptionType,
    subscriptionStatus: data?.subscriptionStatus || null,
    effectiveSubscriptionStatus,
    billingInterval,
    automaticPayments,
    hasManagedSubscription,
    hasRecurringPriceSource,
    canCancelSubscription,
    cancelAtPeriodEnd,
    prepaidDaysRemaining,
    currentPeriodEndsAt,
    nextPaymentAmount,
    activeProductIds,
    subscriptionId,
    familyStripeCustomerId: family?.stripeCustomerId || null,
    familyPaymentMethodId: family?.paymentMethodId || null,
    showCancelSubscriptionButton,
    showMakePaymentButton,
    showLocalPayInPaymentForm,
  });

  return (
    <div className={classes.wrapper}>
      <h4>{translate(336, 'My Account')}</h4>

      {isProductPlan && (
        <label className={classes.labelNote}>
          When Remaining Payments is 0, you must make a single payment to continue using
          <span> {productName}.</span>
        </label>
      )}

      {isLoading || processing ? (
        <CircularProgress />
      ) : (
        <>
          <div>
            <table>
              <tbody>
                {isSubscriptionPlan && (
                  <tr>
                    <th>{subscriptionRow.label}</th>
                    <td>{subscriptionRow.value}</td>
                  </tr>
                )}

                <tr>
                  <th>{translate(3671, 'Active Products')}</th>
                  <td>{activeProductsLabel}</td>
                </tr>

                {!isSubscriptionPlan && (
                  <tr>
                    <th>{translate(0, 'Cost')}</th>
                    <td>
                      {paymentAmount} {currency}
                    </td>
                  </tr>
                )}

                <tr className={cs({ [classes.blink]: prepaidDaysRemaining < 1 })}>
                  <th>{translate(3673, 'Remaining Days')}</th>
                  <td>{prepaidDaysRemaining}</td>
                </tr>

                <tr>
                  <th>{translate(3679, isSubscriptionPlan ? 'Next Renewal Date' : 'End Date')}</th>
                  <td>{currentPeriodEndsAtLabel}</td>
                </tr>

                {isSubscriptionPlan && nextPaymentAmount !== null && (
                  <tr>
                    <th>{translate(3677, 'Next Payment')}</th>
                    <td>
                      {nextPaymentAmount} {currency}
                    </td>
                  </tr>
                )}

                {statusLabel && (
                  <tr>
                    <th>{translate(747, 'Status:')}</th>
                    <td>{statusLabel}</td>
                  </tr>
                )}

                {!isSubscriptionPlan && !data?.fullyPaid && data?.totalPrice ? (
                  <tr>
                    <th>{translate(0, 'Total Cost')}</th>
                    <td>
                      {data.totalPrice} {currency}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            {openPayment && (
              <div className={classes.paymentFormWrapper}>
                <b className={classes.paymentAmount}>
                  Amount - {paymentAmount} {currency}
                </b>

                <Elements stripe={stripePromise}>
                  <StripePaymentForm
                    paymentAmount={paymentAmount}
                    onSuccess={makeStripePayment}
                    onCancel={() => setOpenPayment(false)}
                    translate={translate}
                    family={family}
                    currency={currency}
                  />
                </Elements>

                {showLocalPayInPaymentForm && (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <SettingsButton onClick={startLocalPay}>
                      {translate(0, 'Local Pay')}
                    </SettingsButton>
                  </div>
                )}
              </div>
            )}

            <div className={classes.subscriptionButtonGroup || classes.buttonGroup}>
              {showCancelSubscriptionButton && (
                <SettingsButton onClick={requestPaymentMethod}>
                  {translate(3675, 'Cancel Auto Pay')}
                </SettingsButton>
              )}

              {showMakePaymentButton && (
                <SettingsButton onClick={() => setOpenPayment(true)}>
                  {translate(3674, 'Make Payments')}
                </SettingsButton>
              )}

              {showAdditionalProductsButton && (
                <SettingsButton onClick={handleBuyAdditionalProducts}>
                  {translate(3676, 'BUY ADDITIONAL PRODUCTS')}
                </SettingsButton>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

ManageSubscriptionPrepaid.propTypes = {
  translate: PropTypes.func.isRequired,
  showPromptPromise: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
  showInfo: PropTypes.func.isRequired,
  myAccountValues: PropTypes.object.isRequired,
  getFlinkPlayData: PropTypes.func.isRequired,
};

const mapStateToProps = ({ flinkPlay, status }, ownProps) => ({
  translate: ownProps.translate || flinkPlay.translate || status.translate,
  family: flinkPlay.family,
  myAccountValues: flinkPlay.learningCenter.config.myAccountValues || {},
});

export default connect(mapStateToProps, {
  showPromptPromise,
  showError,
  showInfo,
  getFlinkPlayData,
})(ManageSubscriptionPrepaid);
